/* Shibui Goods — homepage hero: holographic card in WebGL.
   Loads three.js only after the page has loaded and the browser is idle; shows the SVG poster until the first frame.
   Pauses when off-screen or the tab is hidden. Reduced motion: static pose, no idle float. Falls back to the poster silently. */
(function () {
  "use strict";
  var stage = document.querySelector("[data-sg-stage]");
  if (!stage || !("WebGLRenderingContext" in window)) return;
  var canvas = stage.querySelector("canvas");
  var hero = stage.closest(".sg-hero") || stage.parentElement;
  var REDUCE = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var TOUCH = window.matchMedia("(hover: none)").matches;
  var SRC = stage.getAttribute("data-three-src") || "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";

  function ensoPath(cx, cy, r, a0, a1, bw, steps) {
    steps = steps || 160;
    var pts = [], ws = [], seed = 11;
    function rr() { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; }
    for (var i = 0; i <= steps; i++) {
      var t = i / steps, a = (a0 + (a1 - a0) * t) * Math.PI / 180;
      var rad = r * (1 + 0.012 * Math.sin(t * Math.PI * 3.1 + 0.4) + (rr() - 0.5) * 0.018);
      if (t > 0.86) rad *= 1 + 0.05 * Math.pow((t - 0.86) / 0.14, 1.5);
      pts.push([cx + rad * Math.cos(a), cy + rad * Math.sin(a)]);
      var landing = Math.min(1, Math.pow(t / 0.035, 0.5)), thin = 1 - 0.30 * t;
      var taper = t < 0.84 ? 1 : Math.pow(Math.max(0, 1 - (t - 0.84) / 0.16), 1.3);
      var und = 1 + 0.10 * Math.sin(t * Math.PI * 2.6 + 0.9) - 0.06 * Math.sin(t * Math.PI * 6.1 + 0.3);
      ws.push(Math.max(bw * landing * thin * taper * und, bw * 0.05));
    }
    var L = [], R = [];
    for (var j = 0; j < pts.length; j++) {
      var p = pts[j], q = pts[Math.min(j + 1, pts.length - 1)], o = pts[Math.max(j - 1, 0)];
      var tx = q[0] - o[0], ty = q[1] - o[1], len = Math.hypot(tx, ty) || 1, nx = -ty / len, ny = tx / len, w = ws[j] / 2;
      L.push([p[0] + nx * w, p[1] + ny * w]); R.push([p[0] - nx * w, p[1] - ny * w]);
    }
    var all = L.concat(R.reverse()), d = "M " + all[0][0].toFixed(2) + " " + all[0][1].toFixed(2) + " ";
    for (var k = 1; k < all.length; k++) {
      var p0 = all[k - 1], p1 = all[k];
      d += "Q " + p0[0].toFixed(2) + " " + p0[1].toFixed(2) + " " + ((p0[0] + p1[0]) / 2).toFixed(2) + " " + ((p0[1] + p1[1]) / 2).toFixed(2) + " ";
    }
    return d + "Z";
  }


  /* The card front, drawn rather than generated: a night scene in the house palette — moon, three ridgelines, a
     wave band and three cranes — inside a gold frame with a title plate and a card number. Every stroke here is
     ours, so the hero is a Shibui card and not a stand-in for somebody else's. The foil in the shader rides on top
     of this and only inside the art window. */
  function drawFront() {
    var W = 630, H = 880, c = document.createElement("canvas");
    c.width = W; c.height = H;
    var g = c.getContext("2d");
    var SUMI = "#12141A", SURF = "#1A1D22", PAPER = "#ECE9E1", GOLD = "#C9A45C", INDIGO = "#8FB0CE";
    var seed = 7;
    function rnd() { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; }

    var bg = g.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, "#191D24"); bg.addColorStop(1, SUMI);
    g.fillStyle = bg; g.fillRect(0, 0, W, H);

    // gold frame: a solid outer keyline and a hairline just inside it
    g.strokeStyle = GOLD; g.lineWidth = 7; g.strokeRect(16, 16, W - 32, H - 32);
    g.strokeStyle = "rgba(236,233,225,0.22)"; g.lineWidth = 1.4; g.strokeRect(30, 30, W - 60, H - 60);

    // title plate
    g.fillStyle = SURF; g.fillRect(44, 50, W - 88, 74);
    g.strokeStyle = "rgba(201,164,92,0.45)"; g.lineWidth = 1.2; g.strokeRect(44, 50, W - 88, 74);
    g.fillStyle = PAPER;
    g.font = "600 26px 'IBM Plex Mono', ui-monospace, monospace";
    g.textAlign = "left"; g.textBaseline = "middle";
    var t = "S H I B U I   G O O D S";
    g.fillText(t, 66, 88);
    g.fillStyle = GOLD;
    g.font = "600 38px 'Shippori Mincho', serif";
    g.textAlign = "right"; g.fillText("渋", W - 66, 89);

    // ---- art window -------------------------------------------------
    var ax = 44, ay = 140, aw = W - 88, ah = 500;
    g.save();
    g.beginPath(); g.rect(ax, ay, aw, ah); g.clip();

    var sky = g.createLinearGradient(0, ay, 0, ay + ah);
    sky.addColorStop(0, "#16233F"); sky.addColorStop(0.55, "#101A2C"); sky.addColorStop(1, "#0A1018");
    g.fillStyle = sky; g.fillRect(ax, ay, aw, ah);

    for (var i = 0; i < 90; i++) {                       // stars
      var sx = ax + rnd() * aw, sy = ay + rnd() * ah * 0.6, r = rnd() * 1.5 + 0.3;
      g.fillStyle = "rgba(236,233,225," + (0.18 + rnd() * 0.5).toFixed(2) + ")";
      g.beginPath(); g.arc(sx, sy, r, 0, 6.2832); g.fill();
    }

    var mx = ax + aw * 0.70, my = ay + ah * 0.30, mr = 74;   // moon and halo
    var halo = g.createRadialGradient(mx, my, mr * 0.7, mx, my, mr * 3.2);
    halo.addColorStop(0, "rgba(236,233,225,0.20)"); halo.addColorStop(1, "rgba(236,233,225,0)");
    g.fillStyle = halo; g.beginPath(); g.arc(mx, my, mr * 3.2, 0, 6.2832); g.fill();
    g.fillStyle = "rgba(240,238,230,0.95)"; g.beginPath(); g.arc(mx, my, mr, 0, 6.2832); g.fill();
    var maria = [[-20, -14, 15], [16, 20, 11], [24, -22, 8]];
    for (var mi = 0; mi < maria.length; mi++) {
      var m0 = maria[mi], gx0 = mx + m0[0], gy0 = my + m0[1];
      var mg = g.createRadialGradient(gx0, gy0, 0, gx0, gy0, m0[2]);
      mg.addColorStop(0, "rgba(176,187,204,0.13)"); mg.addColorStop(1, "rgba(176,187,204,0)");
      g.fillStyle = mg; g.beginPath(); g.arc(gx0, gy0, m0[2], 0, 6.2832); g.fill();
    }

    function ridge(baseY, amp, fill, k) {                    // one mountain layer
      g.fillStyle = fill;
      g.beginPath(); g.moveTo(ax - 4, ay + ah);
      g.lineTo(ax - 4, baseY);
      for (var x = ax - 4; x <= ax + aw + 4; x += 6) {
        var u = (x - ax) / aw;
        var y = baseY
          - Math.abs(Math.sin(u * 3.1 + k)) * amp
          - Math.abs(Math.sin(u * 7.7 + k * 2.0)) * amp * 0.34
          - Math.sin(u * 13.0 + k) * amp * 0.10;
        g.lineTo(x, y);
      }
      g.lineTo(ax + aw + 4, ay + ah); g.closePath(); g.fill();
    }
    ridge(ay + ah * 0.66, 92, "#1C2A45", 0.6);
    ridge(ay + ah * 0.76, 78, "#141F33", 2.3);
    ridge(ay + ah * 0.87, 62, "#0C1420", 4.1);

    // water: two swells filled to the foot of the window, each with a lit crest
    function swell(baseY, amp, wl, phase, fill, crest, lw) {
      g.beginPath(); g.moveTo(ax - 4, ay + ah);
      g.lineTo(ax - 4, baseY);
      for (var x = ax - 4; x <= ax + aw + 4; x += 4) {
        var u = (x - ax) / aw;
        g.lineTo(x, baseY - Math.sin(u * wl + phase) * amp - Math.sin(u * wl * 2.3 + phase * 1.7) * amp * 0.30);
      }
      g.lineTo(ax + aw + 4, ay + ah); g.closePath();
      g.fillStyle = fill; g.fill();
      g.beginPath();
      for (var x2 = ax - 4; x2 <= ax + aw + 4; x2 += 4) {
        var u2 = (x2 - ax) / aw;
        var y2 = baseY - Math.sin(u2 * wl + phase) * amp - Math.sin(u2 * wl * 2.3 + phase * 1.7) * amp * 0.30;
        if (x2 === ax - 4) g.moveTo(x2, y2); else g.lineTo(x2, y2);
      }
      g.strokeStyle = crest; g.lineWidth = lw; g.stroke();
    }
    swell(ay + ah * 0.905, 13, 9.0, 0.4, "#0A1220", "rgba(143,176,206,0.45)", 1.6);
    swell(ay + ah * 0.955, 11, 12.0, 2.1, "#070C14", "rgba(201,164,92,0.70)", 2.2);

    function crane(cx0, cy0, sc, alpha) {                    // three strokes: two wings and a body
      g.save(); g.translate(cx0, cy0); g.scale(sc, sc);
      g.strokeStyle = "rgba(201,164,92," + alpha + ")"; g.lineWidth = 2.6; g.lineCap = "round";
      g.beginPath(); g.moveTo(-16, 0); g.quadraticCurveTo(-7, -11, 0, -2); g.stroke();
      g.beginPath(); g.moveTo(16, -2); g.quadraticCurveTo(7, -12, 0, -2); g.stroke();
      g.beginPath(); g.moveTo(0, -2); g.lineTo(6, 4); g.stroke();
      g.restore();
    }
    crane(ax + aw * 0.30, ay + ah * 0.24, 1.15, 0.95);
    crane(ax + aw * 0.44, ay + ah * 0.15, 0.85, 0.75);
    crane(ax + aw * 0.20, ay + ah * 0.36, 0.66, 0.55);

    g.restore();
    g.strokeStyle = "rgba(201,164,92,0.55)"; g.lineWidth = 1.6; g.strokeRect(ax, ay, aw, ah);

    // ---- name plate and body ---------------------------------------
    g.fillStyle = PAPER;
    g.font = "600 44px 'Shippori Mincho', serif";
    g.textAlign = "left"; g.textBaseline = "alphabetic";
    g.fillText("渋 の 一 枚", 50, 700);
    g.strokeStyle = "rgba(201,164,92,0.6)"; g.lineWidth = 2;
    g.beginPath(); g.moveTo(50, 716); g.lineTo(W - 50, 716); g.stroke();

    g.fillStyle = "rgba(236,233,225,0.62)";
    g.font = "500 17px 'IBM Plex Mono', ui-monospace, monospace";
    var lines = ["GRADED TO THE WORST FLAW", "PHOTOGRAPHED FRONT AND BACK", "CERT LINKED \u00B7 SLEEVED \u00B7 BOXED"];
    for (var li = 0; li < lines.length; li++) g.fillText(lines[li], 50, 748 + li * 26);

    g.fillStyle = "rgba(166,171,178,0.85)";
    g.font = "500 20px 'IBM Plex Mono', ui-monospace, monospace";
    g.fillText("001 / 001", 50, 848);
    g.textAlign = "right";
    g.fillStyle = GOLD; g.fillText("SG", W - 50, 848);
    return c;
  }

  function init() {
    var THREE = window.THREE;
    if (!THREE) return;
    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: "low-power" });
    } catch (e) { return; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    var scene = new THREE.Scene();
    var cam = new THREE.PerspectiveCamera(28, 4 / 5, 0.1, 10);
    cam.position.set(0, 0, 3.2);

    // Back face: enso + kanji drawn on a canvas texture
    function drawBack() {
      var bc = document.createElement("canvas"); bc.width = 630; bc.height = 880;
      var g = bc.getContext("2d");
      g.fillStyle = "#1A1D22"; g.fillRect(0, 0, 630, 880);
      g.strokeStyle = "#C9A45C"; g.lineWidth = 6; g.strokeRect(22, 22, 586, 836);
      var p2 = new Path2D(ensoPath(48, 48, 39.8, 277, -28, 7.9));
      g.save(); g.translate(315 - 240, 440 - 240); g.scale(5, 5); g.fillStyle = "#C9A45C"; g.fill(p2); g.restore();
      g.strokeStyle = "rgba(236,233,225,0.20)"; g.lineWidth = 1.4; g.strokeRect(36, 36, 558, 808);
      g.fillStyle = "#ECE9E1"; g.font = "600 200px 'Shippori Mincho', serif"; g.textAlign = "center"; g.textBaseline = "middle";
      g.fillText("渋", 315, 455);
      g.strokeStyle = "rgba(201,164,92,0.45)"; g.lineWidth = 1.6;
      g.beginPath(); g.moveTo(150, 726); g.lineTo(480, 726); g.stroke();
      g.fillStyle = "rgba(236,233,225,0.55)";
      g.font = "500 19px 'IBM Plex Mono', ui-monospace, monospace";
      g.textAlign = "center"; g.textBaseline = "alphabetic";
      g.fillText("S H I B U I   G O O D S", 315, 766);
      return bc;
    }
    var backTex = new THREE.CanvasTexture(drawBack());
    var frontTex = new THREE.CanvasTexture(drawFront());
    frontTex.anisotropy = 4; backTex.anisotropy = 4;

    /* Canvas text takes whatever font is loaded at the moment it is drawn; if Shippori Mincho and IBM Plex Mono
       have not arrived yet the card is painted in a generic serif and stays that way. Redraw both faces once the
       two families are really available. */
    if (document.fonts && document.fonts.load) {
      Promise.all([
        document.fonts.load("600 38px 'Shippori Mincho'"),
        document.fonts.load("600 26px 'IBM Plex Mono'")
      ]).then(function () {
        frontTex.image = drawFront(); frontTex.needsUpdate = true;
        backTex.image = drawBack(); backTex.needsUpdate = true;
      })["catch"](function () {});
    }
    var uniforms = { uTime: { value: 0 }, uMouse: { value: new THREE.Vector2(0, 0) }, uMap: { value: frontTex } };
    var front = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: "varying vec2 vUv; varying vec3 vN; varying vec3 vV; void main(){ vUv=uv; vec4 wp=modelMatrix*vec4(position,1.0); vN=normalize(mat3(modelMatrix)*normal); vV=normalize(cameraPosition-wp.xyz); gl_Position=projectionMatrix*viewMatrix*wp; }",
      fragmentShader: [
        "precision mediump float; varying vec2 vUv; varying vec3 vN; varying vec3 vV;",
        "uniform float uTime; uniform vec2 uMouse; uniform sampler2D uMap;",
        "void main(){",
        " vec2 uv=vUv;",
        " vec3 base=texture2D(uMap,uv).rgb;",
        " vec3 gold=vec3(0.788,0.643,0.361);",
        // the art window in canvas pixels (44..586 across, 140..640 down of 630x880), in texture coordinates
        " float inArt=step(0.070,uv.x)*step(uv.x,0.930)*step(0.273,uv.y)*step(uv.y,0.841);",
        " float nd=max(dot(normalize(vN),normalize(vV)),0.0); float fres=pow(1.0-nd,2.2);",
        " float band=sin((uv.x*3.0+uv.y*1.7)*6.0+uTime*0.55+uMouse.x*2.6)*0.5+0.5;",
        " vec3 rainbow=0.5+0.5*cos(6.2831*(band*0.85+nd*1.5+vec3(0.0,0.33,0.67)));",
        " float foil=smoothstep(0.25,1.0,fres*1.25+pow(band,7.0)*0.40)*inArt;",
        " vec3 col=mix(base,rainbow*mix(vec3(0.72,0.82,1.0),gold,0.4),foil*0.55);",
        // a soft sheen sweeping the whole face, so the frame catches light too
        " float sweep=smoothstep(0.35,0.0,abs(fract((uv.x+uv.y*0.35)-uTime*0.06)-0.5));",
        " col+=gold*sweep*0.06;",
        " col+=gold*fres*0.10;",
        " gl_FragColor=vec4(col,1.0); }"
      ].join("\n")
    });
    var edge = new THREE.MeshStandardMaterial({ color: 0xC9A45C, metalness: 0.8, roughness: 0.35 });
    var back = new THREE.MeshBasicMaterial({ map: backTex });
    var mesh = new THREE.Mesh(new THREE.BoxGeometry(0.63, 0.88, 0.014), [edge, edge, edge, edge, front, back]);
    scene.add(mesh);
    var key = new THREE.DirectionalLight(0xE8CD8A, 1.2); key.position.set(2, 3, 3); scene.add(key);
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    var glow = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1.6), new THREE.ShaderMaterial({
      transparent: true, depthWrite: false,
      vertexShader: "varying vec2 vUv; void main(){vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}",
      fragmentShader: "precision mediump float; varying vec2 vUv; void main(){ float d=distance(vUv,vec2(0.5,0.42)); float a=smoothstep(0.55,0.0,d)*0.28; gl_FragColor=vec4(0.788,0.643,0.361,a);}"
    }));
    glow.position.z = -0.3; scene.add(glow);

    var tx = 0, ty = 0, cx = 0, cy = 0, t0 = performance.now(), visible = true, running = !document.hidden, flip = 0, flipT = 0, ready = false;
    function size() {
      var r = stage.getBoundingClientRect();
      if (!r.width || !r.height) return;
      renderer.setSize(r.width, r.height, false);
      cam.aspect = r.width / r.height; cam.updateProjectionMatrix();
    }
    size(); window.addEventListener("resize", size);
    if (window.ResizeObserver) new ResizeObserver(size).observe(stage);
    hero.addEventListener("pointermove", function (e) {
      var r = hero.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width - 0.5) * 2; ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
    });
    hero.addEventListener("pointerleave", function () { tx = 0; ty = 0; });
    if (TOUCH && window.DeviceOrientationEvent) {
      window.addEventListener("deviceorientation", function (e) {
        if (e.gamma == null) return;
        tx = Math.max(-1, Math.min(1, e.gamma / 30)); ty = Math.max(-1, Math.min(1, (e.beta - 45) / 30));
      });
    }
    stage.addEventListener("click", function () { flipT = flipT ? 0 : Math.PI; });
    new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }).observe(stage);
    document.addEventListener("visibilitychange", function () { running = !document.hidden; });

    function frame(now) {
      requestAnimationFrame(frame);
      if (!visible || !running) return;
      var t = (now - t0) / 1000;
      uniforms.uTime.value = t; uniforms.uMouse.value.set(tx, ty);
      cx += (tx - cx) * 0.06; cy += (ty - cy) * 0.06;
      flip += (flipT - flip) * (REDUCE ? 1 : 0.09);
      var idle = REDUCE ? 0 : 1;
      mesh.rotation.y = cx * 0.45 + Math.sin(t * 0.6) * 0.08 * idle + flip;
      mesh.rotation.x = -cy * 0.35 + Math.sin(t * 0.8) * 0.04 * idle;
      mesh.position.y = Math.sin(t * 1.1) * 0.025 * idle;
      mesh.rotation.z = Math.sin(t * 0.5) * 0.02 * idle;
      renderer.render(scene, cam);
      if (!ready) { ready = true; stage.classList.add("is-ready"); }
    }
    requestAnimationFrame(frame);
  }

  function load() {
    var s = document.createElement("script");
    s.src = SRC; s.async = true; s.onload = init;
    document.head.appendChild(s);
  }
  function schedule() {
    if ("requestIdleCallback" in window) window.requestIdleCallback(load, { timeout: 2000 });
    else setTimeout(load, 400);
  }
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule);
})();
